import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  ListFilter, Plus, Upload, Trash2, Eye, Shield, ShieldOff, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "Domains", "Bundles", "Domains/Bundles", "Publisher IDs",
  "Bundles/Domains + Publisher IDs", "CRIDs", "Domains (ADM)",
  "Adomains", "IP Address", "Top-Level Domains", "Keyword CRIDs", "IFA Device IDs",
];

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function ListManagerPage() {
  const { api, isAdmin } = useAuth();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showView, setShowView] = useState(null);
  const [viewItems, setViewItems] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newList, setNewList] = useState({ name: "", category: "", list_type: "blocked", file: null });

  useEffect(() => { fetchLists(); }, []);

  const fetchLists = async () => {
    try {
      const res = await api.get("/lists");
      setLists(res.data);
    } catch { toast.error("Failed to load lists"); }
    finally { setLoading(false); }
  };

  const createList = async () => {
    if (!newList.name || !newList.category || !newList.file) {
      toast.error("Name, category, and file are required");
      return;
    }
    setCreating(true);
    try {
      const formData = new FormData();
      formData.append("name", newList.name);
      formData.append("category", newList.category);
      formData.append("list_type", newList.list_type);
      formData.append("file", newList.file);
      const res = await api.post("/lists", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setLists([res.data, ...lists]);
      setShowCreate(false);
      setNewList({ name: "", category: "", list_type: "blocked", file: null });
      toast.success(`List created with ${res.data.item_count} items`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create list");
    } finally { setCreating(false); }
  };

  const viewList = async (id) => {
    try {
      const res = await api.get(`/lists/${id}`);
      setViewItems(res.data.items || []);
      setShowView(res.data);
    } catch { toast.error("Failed to load list"); }
  };

  const deleteList = async () => {
    try {
      await api.delete(`/lists/${deleteId}`);
      setLists(lists.filter(l => l.id !== deleteId));
      toast.success("List deleted");
    } catch { toast.error("Failed to delete"); }
    finally { setDeleteId(null); }
  };

  return (
    <div className="space-y-6" data-testid="list-manager-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ListFilter className="h-7 w-7 text-accent" /> List Manager
          </h1>
          <p className="text-muted-foreground text-sm">Upload and manage allow/block lists for bid request filtering</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowCreate(true)} data-testid="create-list-btn">
            <Plus className="mr-2 h-4 w-4" /> Upload List
          </Button>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Total Lists</div>
          <p className="text-2xl font-bold">{lists.length}</p>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1 flex items-center gap-1"><Shield className="h-3.5 w-3.5 text-emerald-500" /> Allowed</div>
          <p className="text-2xl font-bold text-emerald-600">{lists.filter(l => l.list_type === "allowed").length}</p>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1 flex items-center gap-1"><ShieldOff className="h-3.5 w-3.5 text-red-500" /> Blocked</div>
          <p className="text-2xl font-bold text-red-600">{lists.filter(l => l.list_type === "blocked").length}</p>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-muted-foreground mb-1">Total Items</div>
          <p className="text-2xl font-bold">{lists.reduce((s, l) => s + (l.item_count || 0), 0).toLocaleString()}</p>
        </Card>
      </div>

      {/* Lists Table */}
      <Card>
        <CardHeader><CardTitle>Lists</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Items</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lists.map(l => (
                  <TableRow key={l.id} data-testid={`list-row-${l.id.slice(0, 8)}`}>
                    <TableCell className="font-medium">{l.name}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{l.category}</Badge></TableCell>
                    <TableCell>
                      {l.list_type === "allowed"
                        ? <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20" variant="outline"><Shield className="h-3 w-3 mr-1" />Allowed</Badge>
                        : <Badge className="bg-red-500/10 text-red-600 border-red-500/20" variant="outline"><ShieldOff className="h-3 w-3 mr-1" />Blocked</Badge>
                      }
                    </TableCell>
                    <TableCell className="text-right font-mono">{(l.item_count || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{l.created_by_name || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => viewList(l.id)}><Eye className="h-4 w-4" /></Button>
                        {isAdmin && <Button size="sm" variant="ghost" onClick={() => setDeleteId(l.id)}><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {lists.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No lists uploaded yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Upload New List</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>List Name *</Label>
              <Input value={newList.name} onChange={e => setNewList({ ...newList, name: e.target.value })} placeholder="e.g. Blocked Domains Q1" data-testid="list-name" />
            </div>
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={newList.category} onValueChange={v => setNewList({ ...newList, category: v })}>
                <SelectTrigger data-testid="list-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>List Type *</Label>
              <Select value={newList.list_type} onValueChange={v => setNewList({ ...newList, list_type: v })}>
                <SelectTrigger data-testid="list-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="allowed">Allowed (Whitelist)</SelectItem>
                  <SelectItem value="blocked">Blocked (Blacklist)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>File (one item per line) *</Label>
              <Input type="file" accept=".txt,.csv" onChange={e => setNewList({ ...newList, file: e.target.files[0] })} data-testid="list-file" />
              <p className="text-xs text-muted-foreground">Upload a .txt or .csv file with one item per line</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createList} disabled={creating} data-testid="submit-list">
              {creating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Uploading...</> : <><Upload className="mr-2 h-4 w-4" />Upload List</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!showView} onOpenChange={() => setShowView(null)}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{showView?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div className="flex gap-2">
              <Badge variant="outline">{showView?.category}</Badge>
              {showView?.list_type === "allowed"
                ? <Badge className="bg-emerald-500/10 text-emerald-600" variant="outline">Allowed</Badge>
                : <Badge className="bg-red-500/10 text-red-600" variant="outline">Blocked</Badge>
              }
              <Badge variant="outline">{showView?.item_count} items</Badge>
            </div>
            <div className="max-h-[400px] overflow-y-auto border rounded p-3 bg-muted/20">
              <pre className="text-xs font-mono whitespace-pre-wrap">{viewItems.join("\n")}</pre>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete List</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete this list. SSPs/DSPs using it will no longer filter against it.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteList} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
